<!--
  Model edit page for `service/:serviceKey/dev-model/:modelKey/:modelId/item/edit`.

  Moved here from views/service/TabulatorPages/ alongside the read page; the
  route NAME (PageServiceModelBreadTabulatorEdit) is unchanged, so every call
  site that pushes to it — BreadTabulatorActionButton included — is untouched.

  The read/update URLs and the redirect-on-success are carried over verbatim.
  What changed is the chrome: a toolbar carrying the record's identity (so you
  can tell WHICH user you are editing without reading the URL), and
  BreadModelForm instead of BreadTabulatorForm.

  Colour is token-only (--app-*/--tbl-*), so dark mode needs no companion block.
-->
<template>
  <v-container fluid class="ed-page">
    <div class="ed-wrap">
      <div class="ed-head">
        <button class="ed-back" title="Back" @click="$router.go(-1)">
          <v-icon size="20">mdi-arrow-left</v-icon>
        </button>

        <div class="ed-head__identity">
          <h1 class="ed-head__title">Edit {{ recordTitle }}</h1>
          <div class="ed-head__meta">
            <span class="ed-chip">{{ model.name.singular }}</span>
            <span class="ed-chip ed-chip--muted">ID {{ modelId }}</span>
          </div>
        </div>

        <v-btn class="ed-view" text @click="goToRead">
          <v-icon size="16" left>mdi-eye-outline</v-icon>
          View record
        </v-btn>
      </div>

      <AError :api="apiRead" />

      <BreadModelForm
        ref="form"
        action="edit"
        :isLoading="apiRead.isLoading"
        :api="api"
        :model="model"
      />
    </div>
  </v-container>
</template>

<script>
import Api from "@/objects/api";
import BreadcrumbTabulator from "@/objects/breadcrumbTabulator";
import Model from "@/objects/model";
import Service from "@/objects/service";
import { mapState } from "vuex";

export default {
  computed: mapState({
    services: (state) => state.service.data.services,
  }),
  data: () => ({
    apiRead: new Api(),
    api: new Api(),
    breadcrumbTabulator: new BreadcrumbTabulator(),
    model: new Model(),
    modelId: null,
    recordTitle: null,
    service: new Service(),
    apiReadUrl: "",
    apiUpdateUrl: "",
  }),
  created() {
    this.model.getByKey(this.$route.params.modelKey);
    this.modelId = this.$route.params.modelId;
    this.recordTitle = `${this.model.name.singular} ${this.modelId}`;
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);
    this.breadcrumbTabulator.addForOperationEdit({
      services: this.services,
      model: this.model,
      modelId: this.modelId,
    });

    if (this.model.apiUrls.apiReadUrl != "") {
      this.apiReadUrl = `${this.$service[this.service.key]}${
        this.model.apiUrls.apiReadUrl
      }/${this.modelId}/read`;
    } else {
      this.apiReadUrl = `${
        this.$service[this.service.key]
      }/v1/en/console/tabulator/model/${this.model.key}/${this.modelId}/read`;
    }

    if (this.model.apiUrls.apiUpdateUrl != "") {
      this.apiUpdateUrl = `${this.$service[this.service.key]}${
        this.model.apiUrls.apiUpdateUrl
      }/${this.modelId}/edit`;
    } else {
      this.apiUpdateUrl = `${
        this.$service[this.service.key]
      }/v1/en/console/tabulator/model/${this.model.key}/${this.modelId}/edit`;
    }

    this.apiRead.setUrl(this.apiReadUrl);
    this.apiRead.setCallbackCompleted((resp) => {
      this.$refs.form.setData(resp);
      // Prefer the model's declared display column; `name` is only the common case.
      const textColumn = this.model.column && this.model.column.text;
      const title = textColumn ? resp[textColumn] : resp.name;
      if (title) this.recordTitle = title;
    });
    this.apiRead.fetch();

    this.api.setMethod(`POST`);
    this.api.setUrl(this.apiUpdateUrl);
    this.api.setCallbackCompleted(() => {
      this.$router.push({
        name: "PageServiceModelBreadTabulatorRead",
        params: {
          serviceKey: this.model.serviceKey,
          modelKey: this.model.key,
          modelId: this.modelId,
        },
      });
      this.$store.dispatch("showMessage", {
        message: "Updated successfully.",
        messageType: "success",
        timeout: 2000,
      });
    });
  },
  methods: {
    goToRead() {
      this.$router.push({
        name: "PageServiceModelBreadTabulatorRead",
        params: {
          serviceKey: this.model.serviceKey,
          modelKey: this.model.key,
          modelId: this.modelId,
        },
      });
    },
  },
};
</script>

<style scoped>
/* See the note on PageServiceModelRead: the console's page ground has no token,
   so it is literal in light and --app-page in dark, as everywhere else. */
.ed-page {
  background-color: #d8dce2;
  font-family: var(--tbl-font);
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
}

.dark-mode .ed-page {
  background-color: var(--app-page);
}

/* The form is a reading task, not a dashboard — a wide card would stretch a
   two-column grid of short text fields across the whole monitor. */
.ed-wrap {
  max-width: 980px;
  margin: 0 auto;
}

.ed-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 4px 0 16px;
  flex-wrap: wrap;
}

.ed-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 10px;
  border: 1px solid var(--app-border);
  background: var(--app-card);
  color: var(--app-text-muted);
  transition: all 0.15s ease;
}

.ed-back:hover {
  background: var(--app-elevated);
  border-color: var(--app-border-strong);
  color: var(--app-text);
}

.ed-head__identity {
  flex: 1;
  min-width: 0;
}

.ed-head__title {
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: -0.2px;
  color: var(--app-text);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ed-head__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 6px;
}

.ed-chip {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 9px;
  border-radius: 6px;
  background: var(--app-accent-soft);
  border: 1px solid var(--app-accent-border);
  color: var(--app-accent-strong);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.ed-chip--muted {
  background: var(--app-card);
  border-color: var(--app-border);
  color: var(--app-text-muted);
  font-weight: 600;
}

.ed-view.v-btn {
  height: 34px;
  border-radius: 10px;
  color: var(--app-text-muted) !important;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: normal;
  text-transform: none;
}

.ed-view.v-btn:hover {
  color: var(--app-accent) !important;
}

@media (max-width: 599px) {
  .ed-page {
    padding: 8px !important;
  }

  .ed-head__title {
    font-size: 1rem;
  }
}
</style>
